import { FC, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { LanguageSelectorComboBox } from '@eluve/blocks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Heading,
  Icon,
  InputLabel,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  useToast,
} from '@eluve/components';
import {
  useAppointmentId,
  useAppointmentLanguages,
  useAppointmentStatus,
  useTenantId,
} from '@eluve/frontend-appointment-hooks';
import { useTenantUserSettings } from '@eluve/frontend-feature-user-settings';
import { useTranslation } from '@eluve/i18n';
import { SupportedLanguagesByCode } from '@eluve/language-utils';
import { useNamedLogger } from '@eluve/logger';
import { useDialog } from '@eluve/utility-hooks';

type AppointmentLanguageSelectorProps = {
  inline?: boolean;
  inputLanguage?: boolean;
  outputLanguage?: boolean;
  regenerateSummary?: boolean;
  onInputLanguageChange?: (newCode: string) => void;
};

/**
 * Allows the user to update the language of the appointment.
 *
 * @param inline determines if the selector should be displayed in an inline style
 * @param inputLanguage determines if the input language selector should be displayed
 * @param outputLanguage determines if the output language selector should be displayed
 * @param regenerateSummary determines if the summary should be regenerated after the output language is changed
 * @param onInputLanguageChange callback function to be called when the input language is changed
 */
export const AppointmentLanguageSelector: FC<
  AppointmentLanguageSelectorProps
> = ({
  inline = false,
  inputLanguage = false,
  outputLanguage = false,
  regenerateSummary = false,
  onInputLanguageChange,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const appointmentId = useAppointmentId();
  const tenantId = useTenantId();
  const { settings: userDefaultSettings } = useTenantUserSettings();
  const appointmentStatus = useAppointmentStatus();

  const logger = useNamedLogger(AppointmentLanguageSelector.name);
  const { isDialogOpen, toggleDialog } = useDialog();
  const apiClient = useApiClient();
  const [selectedOutputLanguage, setSelectedOutputLanguage] = useState<
    string | null
  >(null);

  const onOutputLanguageChange = regenerateSummary
    ? async (newCode: string) => {
        // Trigger summary regeneration
        try {
          const result = await apiClient.llm.summarizeAppointment({
            body: {},
            params: {
              tenantId,
              appointmentId,
            },
          });

          if (result.status !== 201) {
            toast.error(t('appointment.languageSelector.regenerateFailed'));
            logger.error('Failed to regenerate summary after language update', {
              tenantId,
              appointmentId,
              selectedLanguage: newCode,
            });
            return;
          }

          toast.success(t('appointment.languageSelector.regeneratingToast'), {
            description: t(
              'appointment.languageSelector.regeneratingToastDescription',
            ),
          });
        } catch {
          toast.error(t('appointment.languageSelector.regenerateFailed'));
          logger.error(
            'Unexpected error when attempting to regenerate summary after language update',
            {
              tenantId,
              appointmentId,
              selectedLanguage: newCode,
            },
          );
        }
      }
    : undefined;

  const {
    inputLanguageCode,
    outputLanguageCode,
    isUserAppointmentOwner,
    preferredLanguages,
    updateInputLanguage,
    updateOutputLanguage,
    isUpdatingInputLanguage,
    isUpdatingOutputLanguage,
  } = useAppointmentLanguages({
    tenantId,
    appointmentId,
    userDefaultInputLanguage: userDefaultSettings?.inputLanguage,
    userDefaultOutputLanguage: userDefaultSettings?.outputLanguage,
    onInputLanguageChange,
    onOutputLanguageChange,
  });

  const handleNoteLanguageChange = async () => {
    if (!selectedOutputLanguage) {
      logger.error(
        'Output language update dialog open but no selected language',
        {
          tenantId,
          appointmentId,
        },
      );
      toast.error(t('common.somethingWentWrong'));
      return;
    }

    try {
      toggleDialog();
      await updateOutputLanguage(selectedOutputLanguage);
    } catch (error) {
      toast.error(t('common.somethingWentWrong'));
      logger.error('Failed to update note language', {
        tenantId,
        appointmentId,
        selectedLanguage: selectedOutputLanguage,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setSelectedOutputLanguage(null);
    }
  };

  let inputTooltip;
  if (
    inputLanguage &&
    (appointmentStatus === 'ACTIVE' || appointmentStatus === 'COMPLETED')
  ) {
    inputTooltip = (
      <Tooltip>
        <TooltipTrigger asChild>
          <Icon name="Info" />
        </TooltipTrigger>
        <TooltipContent
          align="start"
          alignOffset={-50}
          className="max-w-60 border-none bg-white text-wrap text-text-secondary shadow-md"
        >
          {t('appointment.languageSelector.spokenLanguageWarning')}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={toggleDialog}>
        <DialogContent size="lg">
          <Stack gap={5} className="p-4">
            <Heading size="3xl">
              {t('appointment.languageSelector.notesLanguageWarning')}
            </Heading>
            <Stack gap={2}>
              <Text>
                {t(
                  'appointment.languageSelector.notesLanguageWarningDescription',
                  {
                    language: selectedOutputLanguage
                      ? (SupportedLanguagesByCode[selectedOutputLanguage] ??
                        selectedOutputLanguage)
                      : t('appointment.languageSelector.theSelectedLanguage'),
                  },
                )}
              </Text>
              <Text>
                {t(
                  'appointment.languageSelector.notesLanguageWarningOverwrite',
                )}
              </Text>
            </Stack>
          </Stack>
          <DialogFooter>
            <Button
              variant="ghost"
              className="w-fit bg-bg-quaternary hover:bg-border-primary"
              onClick={toggleDialog}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={handleNoteLanguageChange} className="w-fit">
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Stack gap={5} className="md:flex-row" align="start">
        {inputLanguage && (
          <Stack direction={inline ? 'horizontal' : 'vertical'} gap={2}>
            {inline ? (
              <Stack fullWidth={false} gap={2} direction="horizontal">
                {inputTooltip}
                <Text
                  inline
                  weight="semibold"
                  color="supporting"
                  className="text-nowrap"
                >
                  {t('appointment.languageSelector.spokenLanguageLabel')}
                </Text>
              </Stack>
            ) : (
              <Stack gap={2} direction="horizontal">
                <InputLabel
                  label={t(
                    'appointment.languageSelector.spokenLanguageAriaLabel',
                  )}
                />
                {inputTooltip}
              </Stack>
            )}

            <LanguageSelectorComboBox
              ariaLabelPrefix={t(
                'appointment.languageSelector.spokenLanguageAriaLabel',
              )}
              selectButtonStyles={cn(
                'text-sm',
                inline ? 'font-semibold' : 'font-normal',
                inline
                  ? 'text-text-supporting border-none text-nowrap'
                  : 'rounded-md border border-border-primary bg-bg-primary w-full px-3 py-2 text-text-secondary',
              )}
              disabled={!isUserAppointmentOwner || isUpdatingInputLanguage}
              selectedLanguageCode={inputLanguageCode}
              onSelectedLanguageCode={async (newCode) => {
                try {
                  await updateInputLanguage(newCode);
                } catch (error) {
                  toast.error(t('common.somethingWentWrong'));
                  logger.error('Failed to update input language', {
                    tenantId,
                    appointmentId,
                    newCode,
                    error:
                      error instanceof Error ? error.message : String(error),
                  });
                }
              }}
              preferredLanguages={preferredLanguages}
              showLanguageIcon={false}
            />
          </Stack>
        )}

        {outputLanguage && (
          <Stack direction={inline ? 'horizontal' : 'vertical'} gap={2}>
            {inline ? (
              <Text
                weight="semibold"
                color="supporting"
                className="text-nowrap"
              >
                {t('appointment.languageSelector.notesLanguageLabel')}
              </Text>
            ) : (
              <InputLabel
                label={t('appointment.languageSelector.notesLanguageAriaLabel')}
              />
            )}

            <LanguageSelectorComboBox
              ariaLabelPrefix={t(
                'appointment.languageSelector.notesLanguageAriaLabel',
              )}
              selectButtonStyles={cn(
                'text-sm',
                inline ? 'font-semibold' : 'font-normal',
                inline
                  ? 'text-text-supporting border-none text-nowrap'
                  : 'rounded-md border border-border-primary bg-bg-primary w-full px-3 py-2 text-text-secondary',
              )}
              selectedLanguageCode={outputLanguageCode}
              disabled={!isUserAppointmentOwner || isUpdatingOutputLanguage}
              onSelectedLanguageCode={async (newCode) => {
                if (regenerateSummary) {
                  toggleDialog();
                  setSelectedOutputLanguage(newCode);
                } else {
                  try {
                    await updateOutputLanguage(newCode);
                  } catch (error) {
                    toast.error(t('common.somethingWentWrong'));
                    logger.error('Failed to update output language', {
                      tenantId,
                      appointmentId,
                      newCode,
                      error:
                        error instanceof Error ? error.message : String(error),
                    });
                  }
                }
              }}
              preferredLanguages={preferredLanguages}
              showLanguageIcon={false}
            />
          </Stack>
        )}
      </Stack>
    </>
  );
};
